import { useState, useCallback } from 'react';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import { mapAuthResponseDtoToEntity } from '@entities/auth';
import { useAuth } from '@entities/auth';
import { applyAuthResponse } from '@entities/auth';
import {
  isEmailAlreadyVerifiedError,
  resolveAuthErrorI18nKey,
  type AuthErrorI18nKey,
} from '@entities/auth';

import { env } from '@shared/config/env';

export type CheckEmailResult = 'success' | 'already_verified' | 'error';

interface UseCheckEmailResult {
  isLoading: boolean;
  errorI18nKey: AuthErrorI18nKey | null;
  errorMessage: string | null;
  checkEmail: (token: string) => Promise<CheckEmailResult>;
}

export function useCheckEmail(): UseCheckEmailResult {
  const [isLoading, setIsLoading] = useState(false);
  const [errorI18nKey, setErrorI18nKey] = useState<AuthErrorI18nKey | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { login: authLogin } = useAuth();

  const checkEmail = useCallback(async (token: string): Promise<CheckEmailResult> => {
    setIsLoading(true);
    setErrorI18nKey(null);
    setErrorMessage(null);

    try {
      const apiClient = createApiClient({ baseURL: env.API_BASE_URL });
      const authApi = createAuthApi(apiClient);

      const response = await authApi.checkEmail(token);
      const authResponse = mapAuthResponseDtoToEntity(response);
      await applyAuthResponse(authResponse, authLogin);

      return 'success';
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message : undefined;
      const i18nKey = resolveAuthErrorI18nKey(rawMessage);

      if (i18nKey) {
        setErrorI18nKey(i18nKey);
      } else {
        setErrorMessage(rawMessage || null);
        setErrorI18nKey('errors.verificationFailed');
      }

      if (isEmailAlreadyVerifiedError(rawMessage)) {
        return 'already_verified';
      }

      return 'error';
    } finally {
      setIsLoading(false);
    }
  }, [authLogin]);

  return { isLoading, errorI18nKey, errorMessage, checkEmail };
}
